import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Apple,
  Smartphone,
  Loader2,
  Info,
  AlertTriangle,
  ShieldOff,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  getAppVersionConfig,
  patchAppVersionBucket,
} from "@/services/appVersionService";

/**
 * The store buckets — one per platform. Zenith ships to Google Play and the
 * Apple App Store only. Each carries its own floor, "latest" and store link.
 *
 * `hasBuilds` — Android orders releases by the integer versionCode, so those
 * buckets can gate on it. The App Store gate compares the marketing version;
 * the backend rejects build fields on iOS rather than accepting them and
 * silently never matching.
 */
const BUCKETS = [
  {
    platform: "android",
    store: "play",
    label: "Android — Google Play",
    Icon: Smartphone,
    hasBuilds: true,
    urlPlaceholder:
      "https://play.google.com/store/apps/details?id=com.elementtree.zenith",
  },
  {
    platform: "ios",
    store: "appstore",
    label: "iOS — App Store",
    Icon: Apple,
    hasBuilds: false,
    urlPlaceholder: "https://apps.apple.com/app/id...",
  },
];

/**
 * Server-side warnings, in plain words.
 *
 * Every one of these saves cleanly and returns 200, then quietly does nothing —
 * which is how someone ends up believing a bad build is blocked when it is not.
 */
const WARNING_COPY = {
  floor_above_latest:
    "The minimum is above the latest version, so nobody can reach it — blocking will not take effect.",
  recommended_above_latest:
    "The recommended version is above the latest, so the update prompt will not appear.",
  floor_build_above_latest_build:
    "The minimum build is above the latest build, so blocking will not take effect.",
  min_build_set_without_min_version:
    "A build floor is set but no version floor. Apps shipped before build numbers were reported are judged on the version, so they will not be gated at all.",
  enforce_on_with_no_floor:
    "Blocking is on but no minimum is set, so nobody is blocked.",
  enforce_without_store_url:
    "Blocking is on with no store URL. The app degrades this to a dismissible prompt, because a blocking screen with a dead Update button cannot be escaped.",
};

const VERSION_REGEX = /^\d+(\.\d+){0,3}$/;

/** -1 / 0 / 1. Mirrors the backend's utils/semver.compareVersions. */
const cmpVersion = (a, b) => {
  const pa = String(a || "").split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b || "").split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
};

const toForm = (b) => ({
  latestVersion: b?.latestVersion ?? "",
  recommendedVersion: b?.recommendedVersion ?? "",
  minVersion: b?.minVersion ?? "",
  latestBuild: b?.latestBuild ?? "",
  recommendedBuild: b?.recommendedBuild ?? "",
  minBuild: b?.minBuild ?? "",
  enforce: b?.enforce ?? false,
  isActive: b?.isActive ?? true,
  storeUrl: b?.storeUrl ?? "",
  title: b?.title ?? "",
  message: b?.message ?? "",
  notes: b?.notes ?? "",
});

const buildOrNull = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);

function BucketCard({ meta, bucket, onSaved }) {
  const { platform, store, label, Icon, hasBuilds, urlPlaceholder } = meta;
  const [form, setForm] = useState(toForm(bucket));
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => setForm(toForm(bucket)), [bucket]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  /**
   * Would saving this BLOCK someone who is not blocked today? A first floor
   * counts, and so does turning blocking on over an existing floor.
   */
  const raisesFloor =
    form.isActive &&
    form.enforce &&
    ((form.minVersion &&
      (!bucket?.enforce ||
        !bucket?.minVersion ||
        cmpVersion(form.minVersion, bucket.minVersion) > 0)) ||
      (hasBuilds &&
        form.minBuild !== "" &&
        (!bucket?.enforce ||
          bucket?.minBuild == null ||
          Number(form.minBuild) > Number(bucket.minBuild))));

  const doSave = async () => {
    try {
      setSaving(true);
      const payload = {
        latestVersion: form.latestVersion.trim(),
        recommendedVersion: form.recommendedVersion.trim(),
        minVersion: form.minVersion.trim(),
        enforce: form.enforce,
        isActive: form.isActive,
        storeUrl: form.storeUrl.trim(),
        title: form.title.trim(),
        message: form.message.trim(),
        notes: form.notes.trim(),
        // Never sent for iOS — the backend rejects them there on purpose.
        ...(hasBuilds
          ? {
              latestBuild: buildOrNull(form.latestBuild),
              recommendedBuild: buildOrNull(form.recommendedBuild),
              minBuild: buildOrNull(form.minBuild),
            }
          : {}),
      };
      const saved = await patchAppVersionBucket(platform, store, payload);
      toast.success(`${label} saved`);
      (saved?.warnings || []).forEach((w) =>
        toast.warning(WARNING_COPY[w] || w, { duration: 9000 })
      );
      onSaved?.(saved);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Failed to save");
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  };

  const handleSave = () => {
    const v = (x) => x.trim();
    if (!VERSION_REGEX.test(v(form.latestVersion))) {
      toast.error("Latest version is required and must look like 1.7.3");
      return;
    }
    for (const [key, labelText] of [
      ["recommendedVersion", "Recommended version"],
      ["minVersion", "Minimum version"],
    ]) {
      if (v(form[key]) && !VERSION_REGEX.test(v(form[key]))) {
        toast.error(`${labelText} must look like 1.7.3, or be left blank`);
        return;
      }
    }
    if (v(form.minVersion) && cmpVersion(v(form.minVersion), v(form.latestVersion)) > 0) {
      toast.error("Minimum version cannot be above the latest version.");
      return;
    }
    if (hasBuilds) {
      for (const [key, labelText] of [
        ["latestBuild", "Latest build"],
        ["recommendedBuild", "Recommended build"],
        ["minBuild", "Minimum build"],
      ]) {
        const raw = String(form[key] ?? "").trim();
        if (raw !== "" && !/^\d+$/.test(raw)) {
          toast.error(`${labelText} must be a whole number, or blank`);
          return;
        }
      }
      if (
        form.minBuild !== "" &&
        form.latestBuild !== "" &&
        Number(form.minBuild) > Number(form.latestBuild)
      ) {
        toast.error("Minimum build cannot be above the latest build.");
        return;
      }
    }
    /**
     * A store URL is not optional once the gate is live. "Update now" opens it;
     * blank means the button does nothing, and on a blocking prompt that is a
     * screen the user cannot leave.
     */
    if (form.isActive && !v(form.storeUrl)) {
      toast.error(
        "Store URL is required while the gate is on — without it the Update button does nothing."
      );
      return;
    }

    if (raisesFloor) {
      setConfirmOpen(true);
      return;
    }
    doSave();
  };

  const warnings = bucket?.warnings || [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            {label}
            {!bucket?.configured && (
              <span className="rounded-full border border-border px-2 py-0.5 text-[10px] font-normal text-muted-foreground">
                not set up
              </span>
            )}
          </span>
          <span className="flex items-center gap-4 text-sm font-normal">
            <span className="flex items-center gap-2 text-muted-foreground">
              {form.isActive ? "Gate on" : "Gate off"}
              <Switch
                checked={!!form.isActive}
                onCheckedChange={(x) => set("isActive", x)}
              />
            </span>
            <span
              className={`flex items-center gap-2 ${
                form.enforce ? "text-destructive" : "text-muted-foreground"
              }`}
            >
              {form.enforce ? "Blocking on" : "Blocking off"}
              <Switch
                checked={!!form.enforce}
                onCheckedChange={(x) => set("enforce", x)}
              />
            </span>
          </span>
        </CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        {warnings.length > 0 && (
          <div className="space-y-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3">
            {warnings.map((w) => (
              <p
                key={w}
                className="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300"
              >
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {WARNING_COPY[w] || w}
              </p>
            ))}
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>Latest version *</Label>
            <Input
              placeholder="1.7.3"
              value={form.latestVersion}
              onChange={(e) => set("latestVersion", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              The newest build actually published here. Nothing can block above
              this.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Recommended version</Label>
            <Input
              placeholder="1.7.3"
              value={form.recommendedVersion}
              onChange={(e) => set("recommendedVersion", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Below this, a dismissible prompt. Blank means no prompt.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label>Minimum version</Label>
            <Input
              placeholder="1.5.0"
              value={form.minVersion}
              onChange={(e) => set("minVersion", e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Below this, <strong>blocked</strong> — if blocking is on. Blank
              means nobody is blocked.
            </p>
          </div>
        </div>

        {hasBuilds && (
          <div className="grid gap-4 sm:grid-cols-3 rounded-lg border border-border bg-muted/30 p-3">
            <div className="sm:col-span-3">
              <p className="text-xs text-muted-foreground">
                <strong>Build numbers (optional).</strong> The versionCode this
                store orders releases by. Set these and apps that report their
                build are judged on it instead of the version. Leave the version
                fields filled too — apps shipped before build numbers were
                reported fall back to them.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Latest build</Label>
              <Input
                type="number"
                min={0}
                placeholder="2"
                value={form.latestBuild ?? ""}
                onChange={(e) => set("latestBuild", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Recommended build</Label>
              <Input
                type="number"
                min={0}
                placeholder="2"
                value={form.recommendedBuild ?? ""}
                onChange={(e) => set("recommendedBuild", e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Minimum build</Label>
              <Input
                type="number"
                min={0}
                placeholder="1"
                value={form.minBuild ?? ""}
                onChange={(e) => set("minBuild", e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Store URL {form.isActive && "*"}</Label>
          <Input
            placeholder={urlPlaceholder}
            value={form.storeUrl}
            onChange={(e) => set("storeUrl", e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Where “Update now” sends people. Must be this store's own listing.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Dialog title (optional)</Label>
            <Input
              placeholder="Update required"
              value={form.title}
              onChange={(e) => set("title", e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label>Dialog message (optional)</Label>
            <Input
              placeholder="Please update to keep using Zenith."
              value={form.message}
              onChange={(e) => set("message", e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label>Notes (internal)</Label>
          <Input
            placeholder="e.g. store listing not live yet"
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
          />
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save {label}
          </Button>
        </div>

        {/**
          * Raising a floor is the only action here that can lock people out, it
          * reaches everyone within seconds, and it is two keystrokes away. Make
          * it cost one deliberate acknowledgement that names what happens.
          */}
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Block older {label} builds?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-2 text-sm">
                  <p>
                    Apps below{" "}
                    <strong>
                      {form.minVersion || `build ${form.minBuild}`}
                    </strong>{" "}
                    will show a full-screen prompt that{" "}
                    <strong>cannot be dismissed</strong>. Those users cannot use
                    Zenith until they update.
                  </p>
                  <p className="rounded-md border border-border bg-muted/50 p-2">
                    Only raise this once the fixed build is{" "}
                    <strong>live on this store</strong> and downloadable. Builds
                    shipped before app versions were kept in sync report their
                    version as <code>1.0</code>, so they will be blocked too.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={saving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={doSave}
                disabled={saving}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Yes, block older builds
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}

const AppVersions = () => {
  const [buckets, setBuckets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [killing, setKilling] = useState(false);
  const [killOpen, setKillOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setBuckets(await getAppVersionConfig());
    } catch {
      toast.error("Failed to load app version config");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaved = (saved) =>
    setBuckets((prev) =>
      prev.map((b) =>
        b.platform === saved?.platform && b.store === saved?.store
          ? { ...saved, configured: true }
          : b
      )
    );

  const bucketFor = (meta) =>
    buckets.find(
      (b) => b.platform === meta.platform && b.store === meta.store
    ) || null;

  const anyBlocking = buckets.some((b) => b.enforce && b.isActive);

  /**
   * The panic button. Turns BLOCKING off everywhere without touching the
   * prompts, so people are unblocked immediately but still told an update
   * exists. Takes effect within about 30 seconds.
   */
  const disableAllBlocking = async () => {
    setKilling(true);
    try {
      await Promise.all(
        BUCKETS.map((m) =>
          patchAppVersionBucket(m.platform, m.store, { enforce: false }).catch(
            (err) => {
              // A bucket that was never set up has nothing to unblock.
              if (err?.response?.status === 400) return null;
              throw err;
            }
          )
        )
      );
      toast.success("Blocking disabled on every store.");
      await fetchData();
    } catch (err) {
      toast.error(
        err?.response?.data?.error || "Could not disable blocking everywhere."
      );
    } finally {
      setKilling(false);
      setKillOpen(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">App Versions</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Update prompts in the mobile app, per store.
          </p>
        </div>
        {anyBlocking && (
          <Button
            variant="outline"
            onClick={() => setKillOpen(true)}
            disabled={killing}
            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            {killing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ShieldOff className="mr-2 h-4 w-4" />
            )}
            Disable all blocking
          </Button>
        )}
      </div>

      <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
        <Info className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="space-y-1">
          <p>
            <strong>Gate on/off</strong> decides whether a store is evaluated at
            all. <strong>Blocking</strong> decides whether anyone can be stopped
            — turn it off and people still get the dismissible prompt.
          </p>
          <p>
            Apps released before the update check existed cannot be
            force-updated retroactively — they never ask.
          </p>
          <p>
            A minimum only takes effect once <strong>Latest</strong> is at or
            above it. That guard stops people being pointed at a build the store
            has not published yet.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {BUCKETS.map((meta) => (
            <BucketCard
              key={`${meta.platform}/${meta.store}`}
              meta={meta}
              bucket={bucketFor(meta)}
              onSaved={handleSaved}
            />
          ))}
        </div>
      )}

      <AlertDialog open={killOpen} onOpenChange={setKillOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldOff className="h-5 w-5" />
              Disable blocking on every store?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Nobody will be blocked from using the app. The dismissible update
              prompt keeps working, and no minimum versions are erased — you can
              switch blocking back on per store. Takes effect within about 30
              seconds.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={killing}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={disableAllBlocking} disabled={killing}>
              Disable blocking everywhere
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AppVersions;
